import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { Button, Spinner } from '../../../components/design-system';
import { HandbookView } from '../../../components/handbook/HandbookView';
import type { HandbookContent, Itinerary } from '../../../types';

function isHandbook(c: unknown): c is HandbookContent {
  return !!c && typeof c === 'object' && 'days' in (c as object);
}

export function HandbookPage() {
  const { id: matchId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [handbook, setHandbook] = useState<HandbookContent | null>(null);
  const [hostName, setHostName] = useState<string | null>(null);
  const [hostAvatar, setHostAvatar] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [ratings, setRatings] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!user || !matchId) return;
    // Reset immediately on every matchId change — otherwise switching to a
    // trip with no itinerary yet (or none at all) left the PREVIOUS trip's
    // handbook on screen, since the fetch below only ever set state when a
    // row existed and never had an else-branch to clear it.
    setLoading(true);
    setItinerary(null);
    setHandbook(null);
    setHostName(null);
    setHostAvatar(null);
    setApproveError(null);
    setRatings({});
    let cancelled = false;
    void (async () => {
      const { data: match } = await supabase
        .from('matches')
        .select('host_id, profiles!matches_host_id_fkey(full_name, avatar_url)')
        .eq('id', matchId)
        .eq('traveler_id', user.id)
        .maybeSingle();

      if (cancelled) return;
      if (match) {
        const m = match as unknown as { host_id: string; profiles: { full_name: string | null; avatar_url: string | null } | null };
        setHostName(m.profiles?.full_name ?? null);
        setHostAvatar(m.profiles?.avatar_url ?? null);
      }

      const { data: itin } = await supabase
        .from('itineraries')
        .select('*')
        .eq('match_id', matchId)
        .maybeSingle();

      const { data: rated } = await supabase
        .from('place_ratings')
        .select('place_id, rating')
        .eq('match_id', matchId)
        .eq('traveler_id', user.id);

      if (cancelled) return;
      setRatings(Object.fromEntries((rated ?? []).map((r) => [r.place_id as string, r.rating as number])));
      if (itin) {
        const it = itin as Itinerary;
        setItinerary(it);
        if (isHandbook(it.content)) setHandbook(it.content);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, matchId]);

  async function handleApprove() {
    if (!itinerary || !matchId) return;
    setApproving(true);
    setApproveError(null);
    // Don't optimistically flip to "approved" until the DB write actually lands —
    // otherwise a failed write leaves the UI claiming approval that never saved.
    // Goes through an RPC: travelers have no UPDATE policy on itineraries, so a
    // direct update silently matched zero rows and the approval never saved.
    const { error } = await supabase.rpc('traveler_approve_itinerary', { p_match_id: matchId });
    if (error) {
      console.error('approve itinerary failed', error);
      setApproveError("Couldn't approve just now. Please try again.");
      setApproving(false);
      return;
    }
    setItinerary({ ...itinerary, status: 'approved' });
    setApproving(false);
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  const hostFirstName = hostName?.split(' ')[0] ?? 'Your host';

  if (!itinerary || !handbook || itinerary.status === 'draft') {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center gap-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
          <i className="ti ti-clipboard-list text-[length:calc(26px*var(--text-scale))]" style={{ color: 'var(--accent)' }} />
        </div>
        <div>
          <h2 className="font-serif text-2xl text-app-fg mb-1">Your handbook is on the way</h2>
          <p className="text-sm text-app-muted">{hostFirstName} is crafting your personalised trip plan. You'll see it here once it's ready.</p>
        </div>
      </div>
    );
  }

  // Optimistic: the stars move at once; if saving fails the old value comes back.
  async function ratePlace(placeId: string, rating: number) {
    if (!matchId) return;
    const previous = ratings[placeId];
    setRatings((r) => {
      const next = { ...r };
      if (rating > 0) next[placeId] = rating; else delete next[placeId];
      return next;
    });
    // 0 means "take my rating back".
    const { error } = rating > 0
      ? await supabase.rpc('rate_place', { p_match_id: matchId, p_place_id: placeId, p_rating: rating })
      : await supabase.rpc('clear_place_rating', { p_match_id: matchId, p_place_id: placeId });
    if (error) {
      setRatings((r) => {
        const next = { ...r };
        if (previous) next[placeId] = previous; else delete next[placeId];
        return next;
      });
    }
  }

  const isSubmitted = itinerary.status === 'submitted';
  const isApproved = itinerary.status === 'approved';

  return (
    <HandbookView
      handbook={handbook}
      hostFirstName={hostFirstName}
      hostAvatarUrl={hostAvatar}
      onAskHost={() => navigate(`/trip/${matchId}/chat`)}
      openOnToday
      ratings={ratings}
      onRate={(placeId, rating) => void ratePlace(placeId, rating)}
      headerTop={approveError ? <p className="text-xs text-crimson mb-2">{approveError}</p> : undefined}
      headerRight={
        isApproved ? (
          <span
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full whitespace-nowrap mt-1.5"
            style={{ padding: '5px 12px', fontSize: 'var(--fs-caption)', background: 'rgb(var(--success) / .11)', color: 'var(--success-fg)', border: '0.5px solid rgb(var(--success) / .28)' }}
          >
            <span className="w-[5px] h-[5px] rounded-full bg-success" />
            Approved by you
          </span>
        ) : isSubmitted ? (
          <Button size="sm" onClick={() => void handleApprove()} disabled={approving} className="shrink-0 mt-1.5">
            {approving ? 'Approving…' : 'Approve handbook'}
          </Button>
        ) : undefined
      }
    />
  );
}

export default HandbookPage;
